import { useEffect, useState, useRef } from "react";
import { message, Modal, Input, Row, Col } from "antd";
import "../style.css";
import { useHistory } from "react-router-dom";
import CWBrowserLogo from "../CWBrowserLogo";
import eye from "../../assets/images/eye.png";
import Loader from "./Loader";
import QRCodeSVG from "react-qr-code";
import { getOAuthHeaders } from "../../constants/oAuthValidation";
import { getGlobalParameters, signIn, verifyOtpForLogin } from "../../services/generic";
import messages from "../../errors/messages.en-IN.json";
import TenantSwitcher from "../TenantSwitcher";
import useAuthStore from "../../stores/useAuthStore";
import useSessionStore from "../../stores/useSessionStore";
import usePOSConfigStore from "../../stores/usePOSConfigStore";
import { getTillMode, getTillModeRoute, TILL_MODES } from "../../constants/accessControl";
import { trace } from "../../utils/debugTrace";
import { restoreIdentityCookies, writePosSession } from "../../utils/posSession";

const SignIn = () => {
  const history = useHistory();
  const Instance = process.env.REACT_APP_INSTANCE_TYPE || "Enterprise";
  const domainURL = process.env.REACT_APP_domain;

  const queryParams = new URLSearchParams(window.location.search);
  const username = queryParams.get("username");
  const readOnly = queryParams.get("readOnly");
  const isReadOnly = readOnly === "true";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mfaModalVisible, setMfaModalVisible] = useState(false);
  const [mfaType, setMfaType] = useState("Email OTP");
  const [otpAuthUrl, setOtpAuthUrl] = useState(null);
  const [mfaUserActivityLogId, setMfaUserActivityLogId] = useState("");
  const inputsRef = useRef([]);
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [showTenantSwitcher, setShowTenantSwitcher] = useState(false);
  const [activeTenant, setActiveTenant] = useState(null);

  useEffect(() => {
    setLoading(true);
    check();
    if (window.electronAPI?.tenant) {
      window.electronAPI.tenant.getActive().then(setActiveTenant);
    }
  }, []);

  const check = async () => {
    // BUG-2026: wrap the whole check in try/finally so a throw anywhere in here
    // (not just the two success paths that already call setLoading(false))
    // can never leave the full-card loading overlay stuck covering the form —
    // that overlay is the only thing in this component able to make BOTH
    // Username and Password unresponsive at once.
    try {
      // BUG-2027: an explicit "Switch Organization" always lands here with the flag
      // below set. The target org can still hold its own valid, unexpired session
      // token (independent-per-org sessions, see bc20d21b) — skip SSO auto-bootstrap
      // so the user is always asked to authenticate for the org they switched to.
      const isOrgSwitch = sessionStorage.getItem("cw:orgSwitchPending") === "1";
      if (isOrgSwitch) {
        sessionStorage.removeItem("cw:orgSwitchPending");
      }
      const authHeaders = await getOAuthHeaders();
      trace("SignIn", "check()", { hasAccessToken: !!authHeaders?.access_token, username, isOrgSwitch });
      if (authHeaders?.access_token && !username && !isOrgSwitch) {
        // Auth cookies present (SSO from CW Browser) — bootstrap user data if missing
        const existingUserData = useAuthStore.getState().userData;
        // Cached userData can belong to a different user than the one CW Browser is
        // currently authenticated as (e.g. account switched at the CW Browser shell
        // level without POS's own logout clearing localStorage). Re-bootstrap
        // whenever the cookie identity doesn't match what's cached, not only when
        // userData is completely absent — otherwise the stale user_id gets sent to
        // getTills() for the wrong user.
        // NOTE: the User object's display-name field is "user", not "username".
        const identityMismatch = authHeaders.username && existingUserData?.user && existingUserData.user !== authHeaders.username;
        // BUG-2334: tracked locally (not re-read from the store after setUserData)
        // so it reflects exactly what THIS bootstrap attempt resolved — a stale or
        // mismatched cached id is never carried forward as if it were fresh.
        let resolvedUserId = identityMismatch ? null : existingUserData?.user_id || null;
        if (!existingUserData?.user_id || identityMismatch) {
          trace("SignIn", "SSO bootstrap — fetching getGlobalParameters", { identityMismatch });
          try {
            const globalParametersResponse = await getGlobalParameters();
            if (globalParametersResponse?.status === 200) {
              const parsedResponse = JSON.parse(globalParametersResponse.data.data.getGlobalParameters);
              useAuthStore.getState().setUserData(parsedResponse.User);
              resolvedUserId = parsedResponse.User?.user_id || null;
              trace("SignIn", "SSO bootstrap complete", { userId: parsedResponse.User?.user_id });

              // Defensive: set missing cookies so Till.js auth check passes
              // CW Browser SSO may only inject accessToken/Enterprise — fill gaps from API response
              // BUG-2655: the username cookie gets the LOGIN name (POS record / token), never
              // ssoUser.user, which is the display name the till services do not know.
              const ssoUser = parsedResponse.User;
              restoreIdentityCookies({ userId: ssoUser?.user_id, fallbackName: ssoUser?.user });
            }
          } catch (err) {
            trace("SignIn", "SSO bootstrap failed", { error: err?.message });
          }
        } else {
          // userData exists in store but identity cookies are missing (expired before the token)
          restoreIdentityCookies({ userId: existingUserData?.user_id, fallbackName: existingUserData?.user });
        }
        setLoading(false);
        // Re-read cookies after all bootstrap attempts — only redirect if username/email
        // is now confirmed present. Guards against the /sign-in ↔ /till loop when
        // getGlobalParameters() fails (e.g. missing tenant URL cookies) and the
        // username cookie was never set, causing Till.js to immediately push back.
        const updatedHeaders = getOAuthHeaders();
        // BUG-2334: getGlobalParameters() can return a User with `user` set but
        // `user_id` missing/null (e.g. a stale backend cache entry with no TTL).
        // The defensive cookie-set above only checks `ssoUser?.user`, so
        // updatedHeaders.username can be truthy even though user_id never
        // resolved — that alone used to satisfy this gate and send the user to
        // /till, where Till.js's own getTillData() requires user_id specifically
        // and bounces straight back to /sign-in. Because the bad payload is
        // deterministic (same cache hit every time), that produced an infinite
        // /sign-in ↔ /till reload loop. Require user_id here too, matching
        // Till.js's own gate, so an incomplete SSO payload falls through to the
        // login form instead of a redirect Till.js can never accept.
        if ((updatedHeaders?.username || updatedHeaders?.email) && resolvedUserId) {
          // Releases the Day End Application lock (see cashToKeep.js) — this is the
          // same trust boundary the app already uses elsewhere (cookies are kept
          // alive specifically so this SSO path can proceed to /till).
          useSessionStore.getState().setDayEndAppUrl(null);
          window.location.href = "/till";
        } else {
          setEmail(username || "");
        }
      } else {
        trace("SignIn", "no auth → show login form");
        setEmail(username || "");
      }
    } finally {
      setLoading(false);
    }
  };

  const getDataforEnterprise = async (data) => {
    // A completed explicit login (password or OTP) releases any leftover Day End
    // Application lock (see cashToKeep.js) from a previous close-store cycle.
    useSessionStore.getState().setDayEndAppUrl(null);
    // BUG-2594: the POS's own copy of this login, kept before any cookie is written or read.
    writePosSession({ accessToken: data.accessToken, refreshToken: data.refreshToken, username: data.username, userId: data.userId });
    // Derive cookie expiry from the JWT exp claim so it matches the tenant's
    // actual token lifetime (different per tenant). Fall back to session cookie.
    let expires = "";
    try {
      const payload = JSON.parse(atob(data.accessToken.split(".")[1]));
      if (payload.exp) {
        expires = "expires=" + new Date(payload.exp * 1000).toUTCString();
      }
    } catch (_) {}
    const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
    let domainPart = isLocalhost ? "" : "domain=" + domainURL;
    let securePart = isLocalhost ? "" : "secure=true;";
    document.cookie = "userId=" + data.userId + ";" + expires + ";path=/;" + securePart + "SameSite=Strict;" + domainPart;
    document.cookie = "Enterprise.accessToken=" + data.accessToken + ";" + expires + ";path=/;" + securePart + "SameSite=Strict;" + domainPart;
    document.cookie = "accessToken=" + data.accessToken + ";" + expires + ";path=/;" + securePart + "SameSite=Strict;" + domainPart;
    document.cookie = "Enterprise.refreshToken=" + data.refreshToken + ";" + expires + ";path=/;" + securePart + "SameSite=Strict;" + domainPart;
    document.cookie = "Enterprise=" + true + ";" + expires + ";path=/;" + securePart + "SameSite=Strict;" + domainPart;
    document.cookie = "username=" + data.username + ";" + expires + ";path=/;" + securePart + "SameSite=Strict;" + domainPart;
    localStorage.setItem("refreshToken", data.refreshToken);

    // SSO: Inject auth token into Business & Apps webview partitions
    if (window.electronAPI?.workspace) {
      (async () => {
        try {
          const tenant = await window.electronAPI.tenant.getActive();
          if (tenant?.code) {
            const config = await window.electronAPI.tenant.getWorkspaceConfig(tenant.code);
            const token = data.accessToken;
            // Business webview
            if (config?.businessUrl) {
              window.electronAPI.workspace.prepareWebview(`persist:business-${tenant.code}`, {
                url: config.businessUrl,
                cookies: {
                  __cw_browser: "true",
                  accessToken: token,
                  "Enterprise.accessToken": token,
                  __cw_core_url: config.coreServiceUrl || "",
                  __cw_retail_url: config.retailServiceUrl || "",
                  __cw_auth_url: config.authServiceUrl || "",
                },
              });
            }
            // Apps webview
            if (config?.appsUrl) {
              window.electronAPI.workspace.prepareWebview(`persist:apps-${tenant.code}`, {
                url: config.appsUrl,
                cookies: {
                  __cw_browser: "true",
                  accessToken: token,
                  "Enterprise.accessToken": token,
                  __cw_core_url: config.coreServiceUrl || "",
                  __cw_retail_url: config.retailServiceUrl || "",
                  __cw_auth_url: config.authServiceUrl || "",
                },
              });
            }
          }
        } catch (err) {
          console.warn("[SSO] Failed to inject tokens into workspace webviews:", err.message);
        }
      })();
    }

    let body2 = { token: data.accessToken };
    let body3 = { token: data.accessToken, id: null };

    try {
      var win2 = document.getElementById("myIframe2")?.contentWindow;
      var win3 = document.getElementById("myIframe3")?.contentWindow;
      const globalParametersResponse = await getGlobalParameters();

      if (globalParametersResponse.status === 200) {
        const parsedResponse = JSON.parse(globalParametersResponse.data.data.getGlobalParameters);
        useAuthStore.getState().setUserData(parsedResponse.User);
        const wasLocked = useSessionStore.getState().locked;
        if (wasLocked) {
          useSessionStore.getState().setLocked(false);
          // BUG-1834: this used to hardcode /pos regardless of till mode, so a
          // locked Production/RM-Issue/Estimate till landed on a route it has no
          // access to (ModuleGate bounced it back to /sign-in with no way out
          // except manually editing the URL). Mirror Till.js's own unlock branch.
          const posType = usePOSConfigStore.getState().posType;
          const posConfig = usePOSConfigStore.getState().posConfig;
          const tillMode = getTillMode(posConfig);
          if (posType === "PRD") {
            window.location.href = getTillModeRoute(
              tillMode === TILL_MODES.PRODUCTION_RM_ISSUE ? TILL_MODES.PRODUCTION_RM_ISSUE : TILL_MODES.PRODUCTION
            );
          } else if (tillMode === TILL_MODES.ESTIMATE) {
            window.location.href = "/estimate";
          } else {
            window.location.href = "/pos";
          }
        } else {
          window.location.href = "/till";
        }
      } else {
        setLoading(false);
        displayErrorMessage();
      }
    } catch (error) {
      if (error.message === "Request failed with status code 500") {
        setLoading(false);
        displayErrorMessage();
      }
    }
  };

  const displayErrorMessage = () => {
    message.error({
      content: (
        <div>
          <p>{messages.errors.authentication.session_data_error}</p>
        </div>
      ),
      duration: 10,
      style: { whiteSpace: "pre-wrap" },
    });
  };

  const handleLoginForEnterprise = async (event) => {
    event.preventDefault();
    if (!email || !password) {
      message.error(`${messages.errors.authentication.email_password_required}`);
      return;
    }
    setLoading(true);
    const payload = { username: email, password: password };
    try {
      const res = await signIn(payload);
      if (res.status === 200) {
        const data = res.data;
        if (data.mfaRequired === "Y") {
          setMfaType(data.mfaType);
          setOtpAuthUrl(data.otpAuthUrl ?? null);
          setMfaUserActivityLogId(data.userActivityLogId);
          setMfaModalVisible(true);
          setLoading(false);
          return;
        }
        getDataforEnterprise(res.data);
      } else {
        setLoading(false);
        message.error(`${messages.errors.authentication.session_error}`);
      }
    } catch (error) {
      setLoading(false);
      message.error(`${messages.errors.authentication.invalid_credentials}`);
    }
  };

  const verifyOtp = async () => {
    const enteredOtp = otp.join("");
    if (!enteredOtp.trim()) {
      message.error("Please enter the OTP");
      return;
    }
    const payload = { username: email, userActivityLogId: mfaUserActivityLogId, otp: enteredOtp };
    try {
      const res = await verifyOtpForLogin(payload);
      if (res.status === 200) {
        await getDataforEnterprise(res.data);
        setMfaModalVisible(false);
      } else {
        message.error("Invalid OTP");
      }
    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.message || "OTP verification failed";
      message.error(msg);
    }
  };

  const handleChange = (val, index) => {
    const digit = val.replace(/\D/g, "").slice(0, 1);
    const otpArray = [...otp];
    otpArray[index] = digit;
    setOtp(otpArray);
    if (digit && index < otp.length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (otp[index]) {
        const otpArray = [...otp];
        otpArray[index] = "";
        setOtp(otpArray);
      } else if (index > 0) {
        inputsRef.current[index - 1]?.focus();
      }
    }
  };

  const renderMfaModal = () => {
    const isGoogle = mfaType === "Google Authenticator";
    return (
      <Modal open={mfaModalVisible} onCancel={() => setMfaModalVisible(false)} footer={null} width={380} centered styles={{ body: { padding: "20px 25px" } }}>
        <p style={{ marginBottom: 0, color: "#0F0718", fontWeight: 600, fontSize: "1.1em" }}>Multi-Factor Authentication</p>
        <div className="flex flex-col items-center space-y-4">
          {isGoogle && otpAuthUrl && (
            <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
              <div
                style={{
                  textAlign: "center",
                  marginTop: "2vh",
                  padding: "12px",
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  border: "1px solid #d9d9d9",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                  display: "inline-block",
                }}
              >
                <QRCodeSVG value={otpAuthUrl} size={180} level="M" bgColor="#FFFFFF" fgColor="#000000" marginSize={0} />
              </div>
            </div>
          )}
          <p style={{ marginBottom: 0, marginTop: "1.5vh", textAlign: "center", fontSize: "0.9em", fontWeight: 500 }}>
            {isGoogle ? "Scan the QR code with Google Authenticator, then enter the 6-digit code." : "We have sent a 6-digit OTP to your email. Enter it below."}
          </p>
          <Row justify="center" gutter={[8, 8]} style={{ marginTop: "10px" }}>
            {otp.map((digit, idx) => (
              <Col key={idx}>
                <Input
                  value={digit}
                  maxLength={1}
                  ref={(el) => (inputsRef.current[idx] = el)}
                  onChange={(e) => handleChange(e.target.value, idx)}
                  onKeyDown={(e) => handleKeyDown(e, idx)}
                  style={{ width: "45px", height: "33px", textAlign: "center", fontSize: "18px", borderRadius: "8px", border: "1px solid #ccc", fontWeight: "500" }}
                />
              </Col>
            ))}
          </Row>
          <button
            onClick={verifyOtp}
            style={{
              width: "100%",
              backgroundColor: "#91C507",
              marginTop: "2.2vh",
              color: "#fff",
              borderRadius: "3px",
              height: "6vh",
              border: "none",
              fontSize: "0.9em",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Verify OTP
          </button>
        </div>
      </Modal>
    );
  };

  const handleSignUp = () => {
    setLoading(true);
    window.location.href = "/sign-up";
  };

  const handleForgotPassword = () => {
    setLoading(true);
    window.location.href = "/forgot-password";
  };

  return (
    <>
      {renderMfaModal()}
      <section
        style={{
          height: "100vh",
          width: "100%",
          background: "linear-gradient(135deg, #f0f4f8 0%, #e8edf5 50%, #f5f7fa 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "420px",
            position: "relative",
          }}
        >
          {/* Logo */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "32px" }}>
            <CWBrowserLogo size={28} variant="light" />
          </div>

          {/* Card */}
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              boxShadow: "0 2px 16px rgba(0, 0, 0, 0.08), 0 0 1px rgba(0, 0, 0, 0.1)",
              padding: "36px 32px 28px",
            }}
          >
            <h2
              style={{
                margin: "0 0 4px",
                fontSize: "20px",
                fontWeight: 600,
                color: "#1a1a2e",
                textAlign: "center",
              }}
            >
              Welcome back
            </h2>
            <p
              style={{
                margin: "0 0 24px",
                fontSize: "13px",
                color: "#888",
                textAlign: "center",
              }}
            >
              Sign in to your workspace
            </p>

            {/* Tenant info */}
            {activeTenant && (
              <div
                style={{
                  background: "#f8f9fc",
                  borderRadius: "8px",
                  padding: "10px 14px",
                  marginBottom: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ fontSize: "12px", color: "#555" }}>
                  <span style={{ color: "#999" }}>Organization: </span>
                  <strong style={{ color: "#333" }}>
                    {activeTenant.name && activeTenant.name !== activeTenant.code ? `${activeTenant.name} (${activeTenant.code})` : activeTenant.code}
                  </strong>
                </div>
                <span
                  onClick={() => setShowTenantSwitcher(true)}
                  style={{
                    color: "#5B6FE6",
                    fontSize: "11px",
                    cursor: "pointer",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    marginLeft: "8px",
                  }}
                >
                  Switch
                </span>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleLoginForEnterprise}>
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#444",
                    marginBottom: "6px",
                  }}
                >
                  Username
                </label>
                <input
                  readOnly={isReadOnly}
                  required
                  type="text"
                  placeholder="Enter your username"
                  value={email}
                  id="username"
                  name="username"
                  onChange={(ev) => setEmail(ev.target.value)}
                  style={{
                    width: "100%",
                    height: "42px",
                    borderRadius: "8px",
                    padding: "0 12px",
                    color: "#1a1a2e",
                    border: "1px solid #dde1e6",
                    fontSize: "13px",
                    outline: "none",
                    transition: "border-color 0.2s",
                    boxSizing: "border-box",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#8DC63F")}
                  onBlur={(e) => (e.target.style.borderColor = "#dde1e6")}
                />
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#444",
                    marginBottom: "6px",
                  }}
                >
                  Password
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    required
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(ev) => setPassword(ev.target.value)}
                    style={{
                      width: "100%",
                      height: "42px",
                      padding: "0 40px 0 12px",
                      border: "1px solid #dde1e6",
                      color: "#1a1a2e",
                      borderRadius: "8px",
                      fontSize: "13px",
                      outline: "none",
                      transition: "border-color 0.2s",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#8DC63F")}
                    onBlur={(e) => (e.target.style.borderColor = "#dde1e6")}
                  />
                  <img
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      opacity: 0.5,
                    }}
                    src={eye}
                    alt="Toggle password visibility"
                    width={16}
                  />
                </div>
              </div>

              <button
                id="login"
                name="login"
                type="submit"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  height: "44px",
                  backgroundColor: "#8DC63F",
                  color: "#fff",
                  fontSize: "14px",
                  fontWeight: 600,
                  borderRadius: "8px",
                  border: "none",
                  outline: "none",
                  cursor: "pointer",
                  transition: "background-color 0.2s",
                  letterSpacing: "0.3px",
                }}
                onMouseEnter={(e) => (e.target.style.backgroundColor = "#7DB535")}
                onMouseLeave={(e) => (e.target.style.backgroundColor = "#8DC63F")}
              >
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>

            {Instance !== "Enterprise" && (
              <div
                style={{
                  marginTop: "16px",
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "12px",
                }}
              >
                <a onClick={handleSignUp} style={{ color: "#5B6FE6", cursor: "pointer" }}>
                  Sign Up
                </a>
                <a onClick={handleForgotPassword} style={{ color: "#5B6FE6", cursor: "pointer" }}>
                  Forgot Password?
                </a>
              </div>
            )}
          </div>

          {/* Footer */}
          <p
            style={{
              textAlign: "center",
              fontSize: "11px",
              color: "#aaa",
              marginTop: "24px",
            }}
          >
            CW Browser — Secure Business Workspace
          </p>

          {/* Loading overlay */}
          {loading && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(255, 255, 255, 0.7)",
                borderRadius: "12px",
              }}
            >
              <Loader />
            </div>
          )}
        </div>
      </section>

      <iframe id="myIframe2" style={{ display: "none" }} />
      <iframe id="myIframe3" style={{ display: "none" }} />

      <TenantSwitcher visible={showTenantSwitcher} onClose={() => setShowTenantSwitcher(false)} onSwitch={() => setShowTenantSwitcher(false)} />
    </>
  );
};

export default SignIn;
